import { create } from 'zustand';
import type { ReviewBoqItem, MatchSummary, LvMatch, ReviewData, PlanReviewItem } from '../types';
import { getDiagramReviewData } from '../api/endpoints';

type ReviewSource = 'diagrams' | 'plans';
type DiagramStage = 'collected' | 'grouped' | 'matched';

interface ReviewState {
  // Source toggle
  activeSource: ReviewSource;

  // Diagram pipeline
  diagramRawItems: ReviewBoqItem[] | null;     // from existing prepare (backward compat)
  diagramGroupedItems: ReviewBoqItem[] | null;  // after group/dedup
  diagramMatchedItems: ReviewBoqItem[] | null;  // after LV match
  diagramMatchSummary: MatchSummary | null;
  diagramGroupedAt: string | null;              // ISO timestamp of last grouping
  diagramMatchedAt: string | null;              // ISO timestamp of last matching

  // Plan review
  planItems: PlanReviewItem[];
  planMatchedItems: ReviewBoqItem[] | null;  // flattened from all plans
  planMatchSummary: MatchSummary | null;
  planMatchEditIndex: number | null;
  planExpandedRows: Set<number>;

  // Operation flags
  grouping: boolean;
  matching: boolean;
  publishing: boolean;

  // UI state (diagrams)
  expandedRows: Set<number>;
  matchEditIndex: number | null;

  // Split-view & UI enhancements
  selectedSourcePage: { file: string; page: number; type: 'diagram' | 'plan' } | null;
  expandedCategories: Set<string>;
  expandedPlans: Set<string>;
  showPublishPreview: boolean;
  showPublishPreviewPlans: boolean;

  // Backward compat: old monolithic reviewData
  reviewData: ReviewData | null;
  preparing: boolean;

  // Actions
  setActiveSource: (s: ReviewSource) => void;
  setDiagramGroupedItems: (items: ReviewBoqItem[], groupedAt?: string) => void;
  setDiagramMatchedItems: (items: ReviewBoqItem[], summary: MatchSummary | null, matchedAt?: string) => void;
  setPlanItems: (plans: PlanReviewItem[]) => void;
  updatePlanMatch: (idx: number, items: ReviewBoqItem[], summary: MatchSummary | null) => void;
  rebuildPlanFlattened: () => void;
  setPlanMatchEditIndex: (index: number | null) => void;
  togglePlanRow: (index: number) => void;
  updatePlanItemMatch: (flatIndex: number, lvMatch: LvMatch | null, planIdx: number, itemIdx: number, newSummary: MatchSummary) => void;
  setGrouping: (v: boolean) => void;
  setMatching: (v: boolean) => void;
  setPublishing: (v: boolean) => void;
  toggleRow: (index: number) => void;
  setMatchEditIndex: (index: number | null) => void;
  updateItemMatch: (index: number, lvMatch: LvMatch | null, newSummary: MatchSummary) => void;

  // Split-view & UI enhancements
  setSelectedSourcePage: (page: { file: string; page: number; type: 'diagram' | 'plan' } | null) => void;
  toggleCategory: (category: string) => void;
  togglePlanCard: (planKey: string) => void;
  setShowPublishPreview: (v: boolean) => void;
  setShowPublishPreviewPlans: (v: boolean) => void;

  // Backward compat
  setReviewData: (data: ReviewData | null) => void;
  setPreparing: (v: boolean) => void;
}

export const useReviewStore = create<ReviewState>((set) => ({
  activeSource: 'diagrams',
  diagramRawItems: null,
  diagramGroupedItems: null,
  diagramMatchedItems: null,
  diagramMatchSummary: null,
  diagramGroupedAt: null,
  diagramMatchedAt: null,
  planItems: [],
  planMatchedItems: null,
  planMatchSummary: null,
  planMatchEditIndex: null,
  planExpandedRows: new Set(),
  grouping: false,
  matching: false,
  publishing: false,
  expandedRows: new Set(),
  matchEditIndex: null,
  selectedSourcePage: null,
  expandedCategories: new Set(),
  expandedPlans: new Set(),
  showPublishPreview: false,
  showPublishPreviewPlans: false,
  reviewData: null,
  preparing: false,

  setActiveSource: (activeSource) => set({ activeSource, expandedRows: new Set(), matchEditIndex: null }),

  setDiagramGroupedItems: (diagramGroupedItems, groupedAt) => set({
    diagramGroupedItems,
    diagramGroupedAt: groupedAt || new Date().toISOString(),
    // Clear matched state so stage resets to 'grouped'
    diagramMatchedItems: null,
    diagramMatchSummary: null,
    diagramMatchedAt: null,
    expandedRows: new Set(),
  }),

  setDiagramMatchedItems: (diagramMatchedItems, diagramMatchSummary, matchedAt) =>
    set({
      diagramMatchedItems,
      diagramMatchSummary,
      diagramMatchedAt: matchedAt || new Date().toISOString(),
      expandedRows: new Set(),
    }),

  setPlanItems: (planItems) => set({ planItems }),

  updatePlanMatch: (idx, items, summary) =>
    set((s) => {
      const next = [...s.planItems];
      if (next[idx]) {
        next[idx] = { ...next[idx], matchedItems: items, matchSummary: summary ?? undefined };
      }
      // Rebuild flattened view
      const allItems = next.flatMap((p) => p.matchedItems || []);
      // Merge summaries
      let merged: MatchSummary | null = null;
      const summaries = next.map((p) => p.matchSummary).filter(Boolean) as MatchSummary[];
      if (summaries.length > 0) {
        merged = {
          totalBoqItems: summaries.reduce((a, s) => a + s.totalBoqItems, 0),
          matched: summaries.reduce((a, s) => a + s.matched, 0),
          matchedHigh: summaries.reduce((a, s) => a + s.matchedHigh, 0),
          matchedMedium: summaries.reduce((a, s) => a + s.matchedMedium, 0),
          matchedLow: summaries.reduce((a, s) => a + s.matchedLow, 0),
          unmatched: summaries.reduce((a, s) => a + s.unmatched, 0),
          quantityFlags: summaries.reduce((a, s) => a + s.quantityFlags, 0),
          unmatchedLvPositions: summaries.flatMap((s) => s.unmatchedLvPositions || []),
          duplicateTargets: summaries.flatMap((s) => s.duplicateTargets || []),
        };
      }
      return { planItems: next, planMatchedItems: allItems.length > 0 ? allItems : null, planMatchSummary: merged };
    }),

  rebuildPlanFlattened: () =>
    set((s) => {
      const allItems = s.planItems.flatMap((p) => p.matchedItems || []);
      const summaries = s.planItems.map((p) => p.matchSummary).filter(Boolean) as MatchSummary[];
      let merged: MatchSummary | null = null;
      if (summaries.length > 0) {
        merged = {
          totalBoqItems: summaries.reduce((a, s) => a + s.totalBoqItems, 0),
          matched: summaries.reduce((a, s) => a + s.matched, 0),
          matchedHigh: summaries.reduce((a, s) => a + s.matchedHigh, 0),
          matchedMedium: summaries.reduce((a, s) => a + s.matchedMedium, 0),
          matchedLow: summaries.reduce((a, s) => a + s.matchedLow, 0),
          unmatched: summaries.reduce((a, s) => a + s.unmatched, 0),
          quantityFlags: summaries.reduce((a, s) => a + s.quantityFlags, 0),
          unmatchedLvPositions: summaries.flatMap((s) => s.unmatchedLvPositions || []),
          duplicateTargets: summaries.flatMap((s) => s.duplicateTargets || []),
        };
      }
      return { planMatchedItems: allItems.length > 0 ? allItems : null, planMatchSummary: merged };
    }),

  setPlanMatchEditIndex: (planMatchEditIndex) => set({ planMatchEditIndex }),

  togglePlanRow: (index) =>
    set((s) => {
      const next = new Set(s.planExpandedRows);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { planExpandedRows: next };
    }),

  updatePlanItemMatch: (flatIndex, lvMatch, planIdx, itemIdx, newSummary) =>
    set((s) => {
      // Update in planItems
      const next = [...s.planItems];
      if (next[planIdx]?.matchedItems) {
        const items = [...next[planIdx].matchedItems!];
        items[itemIdx] = { ...items[itemIdx], lvMatch };
        next[planIdx] = { ...next[planIdx], matchedItems: items, matchSummary: newSummary };
      }
      // Rebuild flattened
      const allItems = next.flatMap((p) => p.matchedItems || []);
      const summaries = next.map((p) => p.matchSummary).filter(Boolean) as MatchSummary[];
      let merged: MatchSummary | null = null;
      if (summaries.length > 0) {
        merged = {
          totalBoqItems: summaries.reduce((a, ss) => a + ss.totalBoqItems, 0),
          matched: summaries.reduce((a, ss) => a + ss.matched, 0),
          matchedHigh: summaries.reduce((a, ss) => a + ss.matchedHigh, 0),
          matchedMedium: summaries.reduce((a, ss) => a + ss.matchedMedium, 0),
          matchedLow: summaries.reduce((a, ss) => a + ss.matchedLow, 0),
          unmatched: summaries.reduce((a, ss) => a + ss.unmatched, 0),
          quantityFlags: summaries.reduce((a, ss) => a + ss.quantityFlags, 0),
          unmatchedLvPositions: summaries.flatMap((ss) => ss.unmatchedLvPositions || []),
          duplicateTargets: summaries.flatMap((ss) => ss.duplicateTargets || []),
        };
      }
      return { planItems: next, planMatchedItems: allItems.length > 0 ? allItems : null, planMatchSummary: merged, planMatchEditIndex: null };
    }),

  setGrouping: (grouping) => set({ grouping }),
  setMatching: (matching) => set({ matching }),
  setPublishing: (publishing) => set({ publishing }),

  toggleRow: (index) =>
    set((s) => {
      const next = new Set(s.expandedRows);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { expandedRows: next };
    }),

  setMatchEditIndex: (matchEditIndex) => set({ matchEditIndex }),

  updateItemMatch: (index, lvMatch, newSummary) =>
    set((s) => {
      // Update in diagramMatchedItems if available
      if (s.diagramMatchedItems) {
        const items = [...s.diagramMatchedItems];
        items[index] = { ...items[index], lvMatch };
        return {
          diagramMatchedItems: items,
          diagramMatchSummary: newSummary,
          matchEditIndex: null,
        };
      }
      // Backward compat: update reviewData
      if (s.reviewData) {
        const items = [...s.reviewData.boq_items];
        items[index] = { ...items[index], lvMatch };
        return {
          reviewData: { ...s.reviewData, boq_items: items, match_summary: newSummary },
          matchEditIndex: null,
        };
      }
      return s;
    }),

  // Split-view & UI enhancements
  setSelectedSourcePage: (selectedSourcePage) => set({ selectedSourcePage }),
  toggleCategory: (category) =>
    set((s) => {
      const next = new Set(s.expandedCategories);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return { expandedCategories: next };
    }),
  togglePlanCard: (planKey) =>
    set((s) => {
      const next = new Set(s.expandedPlans);
      if (next.has(planKey)) next.delete(planKey);
      else next.add(planKey);
      return { expandedPlans: next };
    }),
  setShowPublishPreview: (showPublishPreview) => set({ showPublishPreview }),
  setShowPublishPreviewPlans: (showPublishPreviewPlans) => set({ showPublishPreviewPlans }),

  // Backward compat
  setReviewData: (reviewData) => set({
    reviewData,
    // Also populate diagram items for the new UI
    diagramMatchedItems: reviewData?.boq_items ?? null,
    diagramMatchSummary: reviewData?.match_summary ?? null,
    expandedRows: new Set(),
    matchEditIndex: null,
  }),
  setPreparing: (preparing) => set({ preparing }),
}));

interface DiagramReviewData {
  stage?: 'grouped' | 'matched' | null;
  boq_items?: ReviewBoqItem[];
  match_summary?: MatchSummary | null;
  grouped_at?: string;
  matched_at?: string;
}

/**
 * Fetch the current diagram review state and update the store. Used by the
 * Review tab on mount AND by the SSE handler when the agent writes the
 * grouped/matched files mid-run (so the stage advances + table fills live).
 * `preserveView` keeps the user's expanded rows during a live refresh.
 */
export async function loadDiagramReview(opts?: { preserveView?: boolean }): Promise<void> {
  const data = (await getDiagramReviewData()) as DiagramReviewData | null;
  if (!data) return;
  if (data.stage === 'matched' && data.boq_items) {
    useReviewStore.setState({
      diagramGroupedItems: data.boq_items,
      diagramGroupedAt: data.grouped_at || null,
      diagramMatchedItems: data.boq_items,
      diagramMatchSummary: data.match_summary ?? null,
      diagramMatchedAt: data.matched_at || null,
      ...(opts?.preserveView ? {} : { expandedRows: new Set<number>() }),
    });
  } else if (data.stage === 'grouped' && data.boq_items) {
    useReviewStore.getState().setDiagramGroupedItems(data.boq_items, data.grouped_at);
  }
}
